import React from 'react';
import {
    Card,
    CardContent,
    Typography,
    Button,
    Chip,
    Box,
    alpha,
    useTheme,
    Avatar,
} from '@mui/material';
import {
    Quiz as QuizIcon,
    Timer as TimerIcon,
    EmojiEvents as TrophyIcon,
    PlayArrow as PlayIcon,
    Visibility as ViewIcon,
    CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { QuizSummary, QuizAttempt } from 'types/quiz';

interface QuizCardProps {
    quiz: QuizSummary;
    attempt?: QuizAttempt | null;
    onStart?: () => void;
    onViewResults?: () => void;
}

const QuizCardV2: React.FC<QuizCardProps> = ({ quiz, attempt, onStart, onViewResults }) => {
    const theme = useTheme();
    const hasAttempted = !!attempt;
    const isCompleted = attempt?.completedAt !== undefined && attempt?.completedAt !== null;

    const estimatedMinutes = quiz.estimatedTimeSeconds
        ? Math.ceil(quiz.estimatedTimeSeconds / 60)
        : null;

    const statusColor = isCompleted
        ? theme.palette.success.main
        : hasAttempted
            ? theme.palette.warning.main
            : theme.palette.primary.main;

    return (
        <Card
            elevation={0}
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                background: isCompleted
                    ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.03)} 0%, ${alpha(theme.palette.success.main, 0.01)} 100%)`
                    : 'background.paper',
                transition: 'all 0.2s ease',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 24px ${alpha(statusColor, 0.15)}`,
                    borderColor: alpha(statusColor, 0.3),
                },
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: isCompleted
                        ? theme.palette.success.main
                        : hasAttempted
                            ? theme.palette.warning.main
                            : theme.palette.primary.main,
                }
            }}
        >
            <CardContent sx={{ flexGrow: 1, p: 3 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                    <Avatar
                        sx={{
                            bgcolor: alpha(statusColor, 0.1),
                            color: statusColor,
                            width: 48,
                            height: 48,
                        }}
                    >
                        <QuizIcon />
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" fontWeight="700" sx={{ mb: 0.5, lineHeight: 1.3 }}>
                            {quiz.title}
                        </Typography>
                        {isCompleted && (
                            <Chip
                                icon={<CheckIcon />}
                                label="Completed"
                                size="small"
                                color="success"
                                sx={{ height: 22 }}
                            />
                        )}
                        {hasAttempted && !isCompleted && (
                            <Chip
                                label="In Progress"
                                size="small"
                                color="warning"
                                sx={{ height: 22 }}
                            />
                        )}
                    </Box>
                </Box>

                {/* Description */}
                {quiz.description && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            mb: 2,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            minHeight: '40px',
                            lineHeight: 1.6,
                        }}
                    >
                        {quiz.description}
                    </Typography>
                )}

                {/* Meta Info */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                        }}
                    >
                        <QuizIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                        <Typography variant="caption" fontWeight="600" color="primary.main">
                            {quiz.questionCount} Questions
                        </Typography>
                    </Box>
                    {estimatedMinutes !== null && estimatedMinutes > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 2,
                                bgcolor: alpha(theme.palette.secondary.main, 0.08),
                            }}
                        >
                            <TimerIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
                            <Typography variant="caption" fontWeight="600" color="secondary.main">
                                ~{estimatedMinutes} min
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Score Display */}
                {hasAttempted && isCompleted && (
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.success.main, 0.1),
                            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar
                                sx={{
                                    bgcolor: theme.palette.success.main,
                                    color: 'white',
                                    width: 36,
                                    height: 36,
                                }}
                            >
                                <TrophyIcon sx={{ fontSize: 20 }} />
                            </Avatar>
                            <Box>
                                <Typography variant="h6" fontWeight="700" color="success.dark">
                                    {attempt.score} points
                                </Typography>
                                <Typography variant="caption" color="success.dark" fontWeight="500">
                                    Your Score
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* Action Button */}
                <Box sx={{ mt: 3 }}>
                    {!hasAttempted ? (
                        <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            startIcon={<PlayIcon />}
                            onClick={onStart}
                            sx={{
                                borderRadius: 2,
                                py: 1.5,
                                fontWeight: 700,
                                textTransform: 'none',
                                fontSize: '1rem',
                            }}
                        >
                            Start Quiz
                        </Button>
                    ) : isCompleted ? (
                        <Button
                            fullWidth
                            variant="outlined"
                            size="large"
                            startIcon={<ViewIcon />}
                            onClick={onViewResults}
                            sx={{
                                borderRadius: 2,
                                py: 1.5,
                                fontWeight: 700,
                                textTransform: 'none',
                                fontSize: '1rem',
                                borderWidth: 2,
                                '&:hover': {
                                    borderWidth: 2,
                                }
                            }}
                        >
                            View Results
                        </Button>
                    ) : (
                        <Button
                            fullWidth
                            variant="contained"
                            color="warning"
                            size="large"
                            startIcon={<PlayIcon />}
                            onClick={onStart}
                            sx={{
                                borderRadius: 2,
                                py: 1.5,
                                fontWeight: 700,
                                textTransform: 'none',
                                fontSize: '1rem',
                            }}
                        >
                            Continue Quiz
                        </Button>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

export default QuizCardV2;
